"use client";

import { useState, type ReactNode } from "react";
import type { FormState } from "@/modules/shared/forms";
import type { BookDayView } from "./BookAppointment";
import { Banner } from "./ui";

/**
 * The three screens a WhatsApp link opens (R21):
 * docs/ux/screens/BookingCancelScreen.md,
 * docs/ux/screens/BookingRescheduleScreen.md,
 * docs/ux/screens/RescheduleSuggestionScreen.md.
 *
 * **Every one of them acts on a POST from a button, never on the page load.**
 * WhatsApp fetches a preview of every link it sends, so a URL that cancelled an
 * appointment on a GET would cancel it while the message announcing it was
 * still being composed. The link opens a page that states the booking and waits.
 *
 * **Shared by both frontends since R22 T13**, with the commands and the link
 * component injected. That is worth the small ceremony: these three screens are
 * the ones somebody reaches from a chat thread with no app around them, and the
 * copy on them ("rejecting cancels the booking") is the only warning they get
 * before an irreversible act. Two copies of that warning is one copy too many.
 */

/** The commands and the link renderer each host supplies. */
export interface BookingActionCommands {
  cancel: (bookingId: string) => Promise<FormState>;
  reschedule: (
    bookingId: string,
    date: string,
    startMinutes: number,
    endMinutes: number,
  ) => Promise<FormState>;
  acceptSuggestion: (bookingId: string) => Promise<FormState>;
  rejectSuggestion: (bookingId: string) => Promise<FormState>;
  Link: (props: {
    href: string;
    className?: string;
    children: ReactNode;
  }) => ReactNode;
}

/**
 * One command, its pending flag and its refusal, which is all any of these
 * screens needs. `success` is what swaps the screen for its `Done` panel.
 */
function useCommand() {
  const [state, setState] = useState<FormState>({});
  const [pending, setPending] = useState(false);
  const run = (command: () => Promise<FormState>) => {
    setPending(true);
    setState({});
    void command()
      .then(setState)
      .finally(() => setPending(false));
  };
  return { state, pending, run };
}

export interface ActionBookingView {
  id: string;
  serviceName: string;
  businessName: string;
  practitionerName: string | null;
  whenLabel: string;
  locationLabel: string;
  /** The clinic's suggested time, preformatted; null when none is live (H49). */
  suggestedWhenLabel: string | null;
}

function Summary({ booking }: { booking: ActionBookingView }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      <p className="font-semibold text-ink">{booking.whenLabel}</p>
      <p className="mt-1 text-sm text-body">
        {booking.serviceName} · {booking.businessName}
      </p>
      {booking.practitionerName && (
        <p className="mt-0.5 text-sm text-muted">{booking.practitionerName}</p>
      )}
      <p className="mt-1 text-sm text-muted">{booking.locationLabel}</p>
    </div>
  );
}

/** One tap, and it is not the tap that opened the page. */
export function CancelBookingScreen({
  booking,
  commands,
}: {
  booking: ActionBookingView;
  commands: BookingActionCommands;
}) {
  const { Link } = commands;
  const { state, pending, run } = useCommand();

  if (state.success) {
    return (
      <Done
        title="Booking cancelled"
        body={`Your ${booking.serviceName} at ${booking.businessName} is cancelled.`}
        cta={{ href: "/search", label: "Find another time" }}
        Link={Link}
      />
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-ink">Cancel this booking?</h1>
      <Summary booking={booking} />
      {state.formError && <Banner>{state.formError}</Banner>}
      <DangerButton
        label="Yes, cancel it"
        pending="Cancelling…"
        busy={pending}
        onClick={() => run(() => commands.cancel(booking.id))}
      />
      <Link
        href="/bookings"
        className="block text-center text-sm font-medium text-accent"
      >
        Keep my booking
      </Link>
    </div>
  );
}

/**
 * The Consumer picks another open hour (H50). The days offered are the assigned
 * practitioner's derived calendar; whether an hour is still free is discovered
 * when they choose it, exactly as it is when a booking is first requested.
 */
export function RescheduleBookingScreen({
  booking,
  days,
  commands,
}: {
  booking: ActionBookingView;
  days: BookDayView[];
  commands: BookingActionCommands;
}) {
  const { Link } = commands;
  const { state, pending, run } = useCommand();

  if (state.success) {
    return (
      <Done
        title="Your booking was moved"
        body="The clinic can see the new time. Nothing else changed."
        cta={{ href: "/bookings", label: "See your bookings" }}
        Link={Link}
      />
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-ink">Pick another time</h1>
      <Summary booking={booking} />
      {state.formError && <Banner>{state.formError}</Banner>}

      {days.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface p-6 text-center text-sm text-muted">
          There are no other open times in the next two weeks. Cancel this
          booking and search again, or contact the clinic.
        </p>
      ) : (
        <div className="space-y-4">
          {days.map((day) => (
            <div key={day.date}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
                {day.dateLabel}
              </p>
              <div className="flex flex-wrap gap-2">
                {day.blocks.map((block) => (
                  <SlotButton
                    key={`${day.date}-${block.startMinutes}`}
                    label={block.timeLabel}
                    busy={pending}
                    onClick={() =>
                      run(() =>
                        commands.reschedule(
                          booking.id,
                          day.date,
                          block.startMinutes,
                          block.endMinutes,
                        ),
                      )
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Link
        href="/bookings"
        className="block text-center text-sm font-medium text-accent"
      >
        Leave it as it is
      </Link>
    </div>
  );
}

/**
 * The clinic asked to move the appointment (H49). Two answers, and the screen
 * says plainly what each one does: rejecting cancels the booking, and somebody
 * about to press a button deserves to know that before they press it.
 */
export function RescheduleSuggestionScreen({
  booking,
  commands,
}: {
  booking: ActionBookingView;
  commands: BookingActionCommands;
}) {
  const { Link } = commands;
  const accept = useCommand();
  const reject = useCommand();
  const error = accept.state.formError ?? reject.state.formError;

  if (accept.state.success) {
    return (
      <Done
        title="New time confirmed"
        body="Your appointment has moved. The clinic can see it."
        cta={{ href: "/bookings", label: "See your bookings" }}
        Link={Link}
      />
    );
  }
  if (reject.state.success) {
    return (
      <Done
        title="Booking cancelled"
        body={`The new time did not work, so your booking at ${booking.businessName} was cancelled. You can book another time whenever suits you.`}
        cta={{ href: "/search", label: "Find another time" }}
        Link={Link}
      />
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-ink">
        {booking.businessName} suggested a new time
      </h1>

      <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">
          Your booking now
        </p>
        <p className="mt-1 font-semibold text-ink line-through decoration-muted">
          {booking.whenLabel}
        </p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted">
          Suggested
        </p>
        <p className="mt-1 text-lg font-bold text-accent">
          {booking.suggestedWhenLabel}
        </p>
        <p className="mt-3 border-t border-line pt-3 text-sm text-body">
          {booking.serviceName}
          {booking.practitionerName ? ` · ${booking.practitionerName}` : ""} ·{" "}
          {booking.locationLabel}
        </p>
      </div>

      {error && <Banner>{error}</Banner>}

      <button
        type="button"
        disabled={accept.pending || reject.pending}
        onClick={() => accept.run(() => commands.acceptSuggestion(booking.id))}
        className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-primary px-4 py-2.5 font-semibold text-on-primary transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {accept.pending ? "Confirming…" : "Confirm the new time"}
      </button>

      <div className="space-y-2">
        <DangerButton
          label="This does not work for me"
          pending="Cancelling…"
          busy={reject.pending}
          disabled={accept.pending || reject.pending}
          onClick={() => reject.run(() => commands.rejectSuggestion(booking.id))}
        />
        <p className="text-center text-xs text-muted">
          Rejecting cancels the booking. You can book another time afterwards.
        </p>
      </div>

      <Link
        href="/bookings"
        className="block text-center text-sm font-medium text-accent"
      >
        Decide later
      </Link>
    </div>
  );
}

/**
 * The end of every one of these flows. Somebody who arrived from a chat thread
 * has no app around them, so the screen has to say what happened and where to
 * go next rather than quietly returning to a list.
 */
function Done({
  title,
  body,
  cta,
  Link,
}: {
  title: string;
  body: string;
  cta: { href: string; label: string };
  Link: BookingActionCommands["Link"];
}) {
  return (
    <div className="rounded-xl border border-line bg-surface p-8 text-center shadow-sm">
      <p className="text-lg font-bold text-ink">{title}</p>
      <p className="mx-auto mt-2 max-w-xs text-sm text-muted">{body}</p>
      <Link
        href={cta.href}
        className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-on-primary transition hover:brightness-110"
      >
        {cta.label}
      </Link>
    </div>
  );
}

function DangerButton({
  label,
  pending,
  busy,
  disabled,
  onClick,
}: {
  label: string;
  pending: string;
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled ?? busy}
      className="w-full rounded-lg border border-danger/40 px-4 py-2.5 text-sm font-semibold text-danger transition hover:bg-danger/10 disabled:opacity-60"
    >
      {busy ? pending : label}
    </button>
  );
}

function SlotButton({
  label,
  busy,
  onClick,
}: {
  label: string;
  busy: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="rounded-lg border border-accent bg-accent/5 px-3 py-1.5 text-sm font-medium text-accent transition-colors hover:bg-accent hover:text-on-accent disabled:opacity-60"
    >
      {busy ? "Moving…" : label}
    </button>
  );
}
